import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '@/components/ui/Button';

const STORAGE_KEY = 'hakken-onboarding-done';

const STARS = [
  { w: 2, l: '5%', t: '8%', o: 0.3 }, { w: 1, l: '15%', t: '22%', o: 0.2 },
  { w: 2, l: '25%', t: '5%', o: 0.4 }, { w: 1, l: '35%', t: '18%', o: 0.15 },
  { w: 3, l: '45%', t: '12%', o: 0.3 }, { w: 1, l: '55%', t: '3%', o: 0.2 },
  { w: 2, l: '65%', t: '20%', o: 0.35 }, { w: 1, l: '75%', t: '9%', o: 0.25 },
  { w: 2, l: '85%', t: '15%', o: 0.3 }, { w: 1, l: '92%', t: '25%', o: 0.2 },
  { w: 1, l: '8%', t: '40%', o: 0.15 }, { w: 2, l: '20%', t: '35%', o: 0.2 },
  { w: 1, l: '40%', t: '45%', o: 0.1 }, { w: 2, l: '60%', t: '38%', o: 0.25 },
  { w: 1, l: '78%', t: '42%', o: 0.15 }, { w: 3, l: '50%', t: '28%', o: 0.2 },
  { w: 1, l: '30%', t: '52%', o: 0.1 }, { w: 2, l: '70%', t: '55%', o: 0.15 },
  { w: 1, l: '88%', t: '48%', o: 0.2 }, { w: 2, l: '12%', t: '58%', o: 0.1 },
];

const slides = [
  {
    id: 'welcome',
    bg: 'from-[#1a0a2e] via-[#0d1a3a] to-[#0a2a1a]',
    title: 'Bem-vindo ao\nHakkenCity!',
    titleColor: '#FFD700',
    subtitle: 'A cidade é o teu campo de jogo.\nExplora, descobre e conquista!',
    visual: <WelcomeVisual />,
    btnLabel: 'Começar',
    btnVariant: 'gold',
  },
  {
    id: 'map',
    bg: 'from-[#0a1a2e] via-[#0d2040] to-[#0a1a2e]',
    title: 'Explora o Mapa',
    titleColor: '#FFD700',
    subtitle: 'Descobre pontos de interesse na tua cidade. Aproxima-te e faz check-in para ganhar recompensas!',
    visual: <MapVisual />,
    btnLabel: 'Seguinte',
    btnVariant: 'gold',
  },
  {
    id: 'challenge',
    bg: 'from-[#0a2010] via-[#0d1a10] to-[#0a1020]',
    title: 'Inicia o Desafio',
    titleColor: '#4CAF50',
    subtitle: 'Chega perto de um ponto de interesse, toca nele e inicia o desafio. Completa-o para ganhar moedas e XP!',
    visual: <ChallengeVisual />,
    btnLabel: 'Seguinte',
    btnVariant: 'green',
  },
  {
    id: 'missions',
    bg: 'from-[#1a0a0a] via-[#2a0d0d] to-[#1a0a20]',
    title: 'Completa Missões',
    titleColor: '#FF6B35',
    subtitle: 'Aceita missões e desafios para ganhar recompensas especiais. Sobe de nível e desbloqueia medalhas!',
    visual: <MissionsVisual />,
    btnLabel: 'Seguinte',
    btnVariant: 'gold',
  },
  {
    id: 'shop',
    bg: 'from-[#0a1a10] via-[#0d2015] to-[#1a1a0a]',
    title: 'Troca Moedas\npor Ofertas',
    titleColor: '#D4AF37',
    subtitle: 'Usa as moedas que ganhas para resgatar ofertas reais em parceiros locais — cafés, restaurantes e lojas da cidade!',
    visual: <ShopVisual />,
    btnLabel: 'Seguinte',
    btnVariant: 'gold',
  },
  {
    id: 'ready',
    bg: 'from-[#1a0a2e] via-[#0d1a3a] to-[#0a2a1a]',
    title: 'Pronto para\na Aventura?',
    titleColor: '#FFD700',
    subtitle: 'A tua jornada começa agora. Aveiro espera por ti — vai explorar!',
    visual: <ReadyVisual />,
    btnLabel: 'Jogar agora!',
    btnVariant: 'green',
  },
];

function WelcomeVisual() {
  return (
    <div className="relative flex h-full items-center justify-center">
      {/* Coin particles */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute text-2xl"
          style={{ left: `${10 + i * 14}%`, top: '10%' }}
          animate={{ y: [-10, -30, -10], rotate: [0, 20, -20, 0], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2 + i * 0.3, repeat: Infinity, delay: i * 0.2 }}
        >
          🪙
        </motion.div>
      ))}
      {/* Logo */}
      <motion.div
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 12, delay: 0.2 }}
        className="flex flex-col items-center gap-4"
      >
        <img src="/branding/logo-hakkencity.webp" alt="HakkenCity" className="w-48 drop-shadow-2xl" />
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="rounded-2xl px-5 py-2 text-center text-sm font-bold text-white"
          style={{ background: 'rgba(212,175,55,0.2)', border: '1px solid rgba(212,175,55,0.4)' }}
        >
          ✨ Explora · Descobre · Conquista ✨
        </motion.div>
      </motion.div>
      {/* Pirate character */}
      <motion.img
        src="/characters/pirata.png"
        alt=""
        className="absolute -bottom-2 -right-4 h-44 object-contain drop-shadow-2xl"
        initial={{ x: 80, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ type: 'spring', damping: 15, delay: 0.4 }}
      />
    </div>
  );
}

function MapVisual() {
  return (
    <div className="relative flex h-full items-center justify-center">
      {/* Fake map background */}
      <div
        className="absolute inset-0 rounded-2xl opacity-40"
        style={{
          background: `
            linear-gradient(rgba(52,152,219,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(52,152,219,0.1) 1px, transparent 1px),
            linear-gradient(135deg, #1a2a3a 0%, #0d1a2e 100%)
          `,
          backgroundSize: '40px 40px, 40px 40px, 100% 100%',
        }}
      />
      {/* Map card mockup */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 14, delay: 0.2 }}
        className="parchment-card relative w-64 p-4"
      >
        <div className="mb-3 overflow-hidden rounded-xl bg-[#b8d4e8]" style={{ height: 100 }}>
          {/* Fake map */}
          <svg viewBox="0 0 200 100" className="h-full w-full">
            <rect width="200" height="100" fill="#c8dcea"/>
            <rect x="0" y="40" width="200" height="20" fill="#d4e8d4"/>
            <rect x="80" y="0" width="40" height="100" fill="#d4e8d4"/>
            <rect x="20" y="20" width="50" height="30" fill="#e8e0c8" rx="3"/>
            <rect x="130" y="50" width="40" height="25" fill="#e8e0c8" rx="3"/>
            <rect x="30" y="65" width="35" height="25" fill="#e8e0c8" rx="3"/>
            {/* POI markers */}
            <circle cx="100" cy="50" r="8" fill="#D4AF37" opacity="0.9"/>
            <text x="100" y="54" textAnchor="middle" fontSize="8" fill="white">🏛</text>
            <circle cx="55" cy="30" r="6" fill="#9B59B6" opacity="0.8"/>
            <circle cx="150" cy="70" r="6" fill="#E74C3C" opacity="0.8"/>
          </svg>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xl">📍</span>
          <div>
            <div className="text-xs font-bold text-pirate-dark">Igreja da Misericórdia</div>
            <div className="text-[10px] text-pirate-dark/60">234 m · Centro Histórico</div>
          </div>
        </div>
        <div className="mt-2 text-xs font-bold" style={{ color: '#D4AF37' }}>+120 moedas</div>
      </motion.div>
      {/* Floating poi pins */}
      {[
        { emoji: '🏛', color: '#D4AF37', x: '15%', y: '20%', delay: 0.5 },
        { emoji: '🎭', color: '#9B59B6', x: '75%', y: '15%', delay: 0.7 },
        { emoji: '🍽', color: '#E74C3C', x: '80%', y: '70%', delay: 0.9 },
      ].map((pin, i) => (
        <motion.div
          key={i}
          className="absolute flex h-10 w-10 items-center justify-center rounded-full text-lg shadow-lg"
          style={{ left: pin.x, top: pin.y, background: pin.color, border: '3px solid white' }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1, y: [0, -5, 0] }}
          transition={{ delay: pin.delay, y: { duration: 2, repeat: Infinity, delay: pin.delay } }}
        >
          {pin.emoji}
        </motion.div>
      ))}
    </div>
  );
}

function ChallengeVisual() {
  const [phase, setPhase] = useState('card'); // 'card' | 'reward'
  useEffect(() => {
    const t = setInterval(() => setPhase((v) => (v === 'card' ? 'reward' : 'card')), 2800);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative flex h-full items-center justify-center">
      <AnimatePresence mode="wait">
        {phase === 'card' ? (
          <motion.div
            key="poi-card"
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0, y: -20 }}
            transition={{ type: 'spring', damping: 14 }}
            className="parchment-card w-64 p-4"
          >
            {/* Category label */}
            <div className="mb-2 text-[9px] font-black tracking-widest text-pirate-dark/50 uppercase">
              História e Cultura
            </div>
            {/* POI header */}
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-2xl overflow-hidden"
                style={{ background: 'rgba(212,175,55,0.15)', border: '2px solid #D4AF37' }}>
                🏛
              </div>
              <div>
                <div className="font-bold text-pirate-dark text-sm leading-tight">Igreja da Misericórdia de Aveiro</div>
                <div className="text-[10px] text-pirate-dark/60 mt-0.5">📍 235 m — Centro Histórico</div>
                <div className="mt-1 flex gap-1.5">
                  <span className="rounded-full px-2 py-0.5 text-[9px] font-bold text-white" style={{ background: '#D4AF37' }}>+120 moedas</span>
                  <span className="rounded-full px-2 py-0.5 text-[9px] font-bold text-white" style={{ background: '#3A8FD1' }}>+98 XP</span>
                </div>
              </div>
            </div>
            {/* Difficulty */}
            <div className="mb-3 flex items-center justify-between border-t border-pirate-dark/10 pt-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-pirate-dark/50">Dificuldade</span>
              <span className="text-[11px] font-black text-amber-600">MÉDIA ★★</span>
            </div>
            {/* Hint */}
            <div className="mb-3 rounded-lg border border-amber-400/40 bg-amber-50/60 px-3 py-2 text-center text-[10px] font-bold text-amber-800">
              Vence o jogo da memória para validar a visita
            </div>
            {/* CTA button */}
            <motion.div
              className="btn-gold flex w-full items-center justify-center gap-2 text-sm"
              animate={{ scale: [1, 1.03, 1] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            >
              → INICIAR DESAFIO
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            key="reward"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', damping: 12 }}
            className="parchment-card w-64 p-5 text-center"
          >
            <motion.div
              className="mb-2 text-4xl"
              animate={{ rotate: [-8, 8, -8], scale: [1, 1.15, 1] }}
              transition={{ duration: 0.7, repeat: Infinity }}
            >
              🎯
            </motion.div>
            <div className="mb-1 font-serif text-lg font-bold text-pirate-dark">Desafio completo!</div>
            <div className="mb-3 text-xs text-pirate-dark/60">Igreja da Misericórdia</div>
            <div className="mb-4 flex justify-center gap-6">
              <div className="text-center">
                <div className="text-2xl">🪙</div>
                <div className="font-serif text-xl font-bold text-pirate-dark">+120</div>
                <div className="text-[10px] text-pirate-dark/50">Moedas</div>
              </div>
              <div className="text-center">
                <div className="text-2xl">⭐</div>
                <div className="font-serif text-xl font-bold text-pirate-dark">+98</div>
                <div className="text-[10px] text-pirate-dark/50">XP</div>
              </div>
            </div>
            <div className="btn-green w-full text-sm">Continuar</div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Coins flying on reward */}
      <AnimatePresence>
        {phase === 'reward' && [...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="pointer-events-none absolute text-xl"
            style={{ left: `${20 + i * 12}%`, top: '40%' }}
            initial={{ y: 0, opacity: 1 }}
            animate={{ y: -90, opacity: 0 }}
            exit={{}}
            transition={{ duration: 1.1, delay: i * 0.1 }}
          >
            🪙
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

function MissionsVisual() {
  return (
    <div className="relative flex h-full flex-col items-center justify-center gap-3 px-4">
      {[
        { icon: '🗺', title: 'Descobrir 5 POIs', reward: '+200 moedas', progress: 60, color: '#D4AF37' },
        { icon: '🎯', title: 'Visitar o Centro Histórico', reward: '+150 moedas', progress: 100, color: '#4CAF50' },
        { icon: '🏆', title: 'Subir ao Nível 5', reward: '+500 XP', progress: 35, color: '#9B59B6' },
      ].map((m, i) => (
        <motion.div
          key={i}
          initial={{ x: -40, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2 + i * 0.15, type: 'spring', damping: 15 }}
          className="parchment-card w-full max-w-[260px] p-3"
        >
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xl"
              style={{ background: `${m.color}22`, border: `2px solid ${m.color}` }}
            >
              {m.icon}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-bold text-pirate-dark">{m.title}</div>
              <div className="text-[10px]" style={{ color: m.color }}>{m.reward}</div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-pirate-dark/10">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: m.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${m.progress}%` }}
                  transition={{ delay: 0.4 + i * 0.15, duration: 0.8 }}
                />
              </div>
            </div>
            {m.progress === 100 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.8, type: 'spring' }}
                className="text-xl"
              >
                ✅
              </motion.span>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function ShopVisual() {
  const [redeemed, setRedeemed] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setRedeemed((v) => !v), 2600);
    return () => clearInterval(t);
  }, []);

  const offers = [
    { emoji: '☕', name: 'Café Central', title: 'Café + Pastel', discount: '-50%', cost: 80, color: '#8B4513' },
    { emoji: '🍕', name: 'Restaurante Mar', title: 'Prato do dia', discount: '-2€', cost: 150, color: '#E74C3C' },
    { emoji: '🛍', name: 'Loja Arte', title: 'Postal de Aveiro', discount: 'Oferta', cost: 60, color: '#9B59B6' },
  ];

  return (
    <div className="relative flex h-full flex-col items-center justify-center gap-2 px-2">
      {/* Coins balance */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-1 flex items-center gap-2 rounded-full border border-gold/40 bg-pirate-dark/60 px-4 py-1.5"
      >
        <span className="text-lg">🪙</span>
        <motion.span
          className="font-serif text-base font-black text-gold"
          animate={{ scale: redeemed ? [1, 1.2, 1] : 1 }}
          transition={{ duration: 0.4 }}
        >
          {redeemed ? '920' : '1090'}
        </motion.span>
        <span className="text-xs font-black uppercase text-white/50">moedas</span>
      </motion.div>

      {offers.map((o, i) => (
        <motion.div
          key={i}
          initial={{ x: -30, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.15 + i * 0.12, type: 'spring', damping: 15 }}
          className="parchment-card w-full max-w-[270px] overflow-hidden p-0"
          style={i === 0 ? { boxShadow: '0 0 0 1.5px rgba(45,122,45,0.6), 0 4px 16px rgba(0,0,0,0.3)' } : {}}
        >
          <div className="flex items-center gap-3 p-3">
            {/* Icon */}
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-2xl"
              style={{ background: `${o.color}22`, border: `2px solid ${o.color}66` }}
            >
              {o.emoji}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-black uppercase tracking-wide text-pirate-dark/50">{o.name}</div>
              <div className="truncate text-sm font-bold text-pirate-dark">{o.title}</div>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className="rounded-full bg-forest px-2 py-0.5 text-[9px] font-black text-white">{o.discount}</span>
                <span className="font-serif text-xs font-black text-gold-dark">🪙 {o.cost}</span>
              </div>
            </div>
            {/* Button / redeemed state */}
            <AnimatePresence mode="wait">
              {i === 0 && redeemed ? (
                <motion.div
                  key="qr"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-xl"
                  style={{ background: 'rgba(45,122,45,0.15)', border: '1.5px solid #2D7A2D' }}
                >
                  📱
                </motion.div>
              ) : (
                <motion.button
                  key="btn"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  className="shrink-0 rounded-lg px-2 py-1.5 text-[10px] font-black text-white"
                  style={{
                    background: i === 0 ? 'linear-gradient(135deg,#4CAF50,#2D7A2D)' : 'rgba(255,255,255,0.15)',
                    opacity: i === 0 ? 1 : 0.5,
                  }}
                >
                  {i === 0 ? 'Resgatar' : 'Resgatar'}
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      ))}

      {/* QR popup hint */}
      <AnimatePresence>
        {redeemed && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="mt-1 flex items-center gap-2 rounded-xl border border-gold/30 bg-pirate-dark/80 px-4 py-2"
          >
            <span className="text-xl">📱</span>
            <span className="text-xs font-bold text-white/80">QR gerado! Mostra na loja para validar.</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ReadyVisual() {
  return (
    <div className="relative flex h-full items-center justify-center">
      {/* Star burst */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute text-xl"
          style={{
            left: '50%',
            top: '50%',
            transformOrigin: 'center',
          }}
          animate={{
            x: Math.cos((i / 8) * Math.PI * 2) * 90,
            y: Math.sin((i / 8) * Math.PI * 2) * 90,
            opacity: [0, 1, 0],
            scale: [0.5, 1.2, 0.5],
          }}
          transition={{ duration: 2.5, repeat: Infinity, delay: i * 0.15 }}
        >
          ⭐
        </motion.div>
      ))}
      <motion.div
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 10, delay: 0.2 }}
        className="flex flex-col items-center gap-3 text-center"
      >
        <motion.div
          className="text-6xl"
          animate={{ rotate: [-5, 5, -5], scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          🏴‍☠️
        </motion.div>
        <div className="parchment-card px-6 py-3">
          <div className="font-serif text-lg font-bold text-pirate-dark">Cidade desbloqueada!</div>
          <div className="text-xs text-pirate-dark/70 mt-1">Aveiro aguarda o teu regresso</div>
          <div className="mt-2 flex justify-center gap-3 text-sm">
            <span>🪙 <b className="text-pirate-dark">1090</b></span>
            <span>⭐ <b className="text-pirate-dark">Nível IV</b></span>
          </div>
        </div>
      </motion.div>
      {/* Pirate */}
      <motion.img
        src="/characters/pirata.png"
        alt=""
        className="absolute -bottom-2 -right-2 h-40 object-contain drop-shadow-2xl"
        initial={{ x: 60, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ type: 'spring', damping: 15, delay: 0.5 }}
      />
    </div>
  );
}

function ProgressDots({ total, current }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }, (_, i) => (
        <motion.div
          key={i}
          className="rounded-full"
          animate={{
            width: i === current ? 20 : 8,
            background: i === current ? '#D4AF37' : 'rgba(255,255,255,0.3)',
          }}
          style={{ height: 8 }}
          transition={{ type: 'spring', damping: 20 }}
        />
      ))}
    </div>
  );
}

export default function OnboardingOverlay() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const done = localStorage.getItem(STORAGE_KEY);
    if (!done) setVisible(true);
  }, []);

  const finish = () => {
    setExiting(true);
    setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, '1');
      setVisible(false);
    }, 400);
  };

  const next = () => {
    if (step < slides.length - 1) {
      setStep((s) => s + 1);
    } else {
      finish();
    }
  };

  if (!visible) return null;

  const slide = slides[step];

  return (
    <AnimatePresence>
      {!exiting && (
        <motion.div
          key="onboarding-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[200] overflow-hidden"
        >
          {/* Animated background */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`bg-${step}`}
              className={`absolute inset-0 bg-gradient-to-b ${slide.bg}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            />
          </AnimatePresence>

          {/* Subtle star field */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {STARS.map((s, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-white"
                style={{ width: s.w, height: s.w, left: s.l, top: s.t, opacity: s.o }}
              />
            ))}
          </div>

          <div className="relative flex h-full flex-col">
            {/* Skip button */}
            {step < slides.length - 1 && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute right-4 top-4 z-10 rounded-full px-3 py-1 text-xs font-bold text-white/60"
                style={{ background: 'rgba(255,255,255,0.1)' }}
                onClick={finish}
              >
                Saltar
              </motion.button>
            )}

            {/* Visual area */}
            <div className="flex-1 px-6 pt-12">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`visual-${step}`}
                  className="relative h-full"
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ type: 'spring', damping: 20, stiffness: 200 }}
                >
                  {slide.visual}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Bottom card */}
            <motion.div
              className="rounded-t-3xl px-6 pb-10 pt-5"
              style={{
                background: 'linear-gradient(180deg, rgba(15,15,30,0.97) 0%, #08081a 100%)',
                borderTop: '1px solid rgba(212,175,55,0.3)',
                paddingBottom: `calc(2.5rem + env(safe-area-inset-bottom, 0px))`,
              }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={`text-${step}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <h2
                    className="mb-2 whitespace-pre-line font-serif text-2xl font-bold leading-tight"
                    style={{ color: slide.titleColor, textShadow: `0 0 20px ${slide.titleColor}66` }}
                  >
                    {slide.title}
                  </h2>
                  <p className="mb-5 text-sm leading-relaxed text-white/70">
                    {slide.subtitle}
                  </p>
                </motion.div>
              </AnimatePresence>

              <div className="flex items-center justify-between">
                <ProgressDots total={slides.length} current={step} />
                <motion.div whileTap={{ scale: 0.95 }}>
                  <Button variant={slide.btnVariant} onClick={next}>
                    {slide.btnLabel} →
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
